import React, { useCallback, useEffect, useRef } from 'react';
import styles from './button.module.scss';
import cx from 'classnames';

type ButtonProps = {
  children: any;
  className?: string;
  onClick: any;
  disabled?: boolean;
  style?: React.CSSProperties;
  type?: 'button' | 'submit';
  mode?: 'outline' | 'solid';
  circle?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'auto';
  color?: 'primary' | 'black' | 'white' | 'light-grey' | 'transparent';
  rounded?: 'rounded-sm' | 'rounded-md' | 'rounded-lg' | 'rounded-xl';
  fullWidth?: boolean;
  tabIndex?: number;
  title?: string;
};

const Button = (props: ButtonProps) => {
  const {
    children,
    onClick,
    className,
    disabled,
    style,
    mode = 'solid',
    size = 'sm',
    color = 'transparent',
    circle = false,
    fullWidth = false,
    rounded,
    type = 'button',
    tabIndex,
    title,
  } = props;

  const buttonClass = cx(
    styles.button,
    styles[mode],
    styles[color],
    styles[size],
    { [styles.circle]: circle },
    { [styles.fullWidth]: fullWidth },
    rounded && styles[rounded],
    className
  );

  const ref = useRef<HTMLButtonElement>(null);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if ((event.code === 'Space' || event.code === 'Enter') && ref.current?.contains(document.activeElement)) {
      onClick();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [ref.current?.contains(document.activeElement)]);

  return (
    <button
      ref={ref}
      role="button"
      tabIndex={tabIndex}
      type={type}
      className={buttonClass}
      disabled={disabled}
      onClick={onClick}
      style={style}
      title={title}
    >
      {children}
    </button>
  );
};

export default Button;
